import { ROUTES } from '@/constants/routes'
import { USER_ROLES } from '@/constants/roles'
import type { RoleEntry } from '@/stores/auth'

/**
 * Where to send a user after login / when an authed user hits guest-only pages.
 *
 * 2026-07-16 policy: **never auto-send anyone to Lumen**.
 * Firebase (family) and Laravel (Lumen) sessions are separate; auto-redirect
 * left staff stuck on Lumen login. Staff open Lumen only via explicit UI.
 */
export function isOrgStaffRole(roles: RoleEntry[]): boolean {
  return roles.some((r) => r.role === USER_ROLES.ORG_ADMIN || r.role === USER_ROLES.STAFF)
}

export function isFamilyLinkedRole(roles: RoleEntry[], managedProfiles?: Array<{ role: string }> | null): boolean {
  const FAMILY_LINKED = new Set([
    USER_ROLES.FAMILY_ADMIN,
    USER_ROLES.CO_CARER,
    'teacher',
    'therapist',
    'medical',
    'first_responder',
  ])
  if (roles.some((r) => FAMILY_LINKED.has(r.role))) return true
  return (managedProfiles ?? []).some((p) => FAMILY_LINKED.has(p.role))
}

/** Reject open redirects (external URLs). Internal paths only. */
function safeInternalPath(redirect: string | null | undefined): string | null {
  if (!redirect) return null
  const path = redirect.trim()
  if (!path.startsWith('/')) return null
  if (path.startsWith('//')) return null
  if (path.includes('://')) return null
  return path
}

/**
 * Post-login destination — always in-app.
 * Legacy `/org…` redirects map to `/home` (not Lumen).
 */
export function resolvePostLoginPath(
  _roles: RoleEntry[],
  redirect?: string | null,
): string {
  const safe = safeInternalPath(redirect)
  if (safe) {
    // Old staff deep links → family home (chooser / CTA lives there)
    if (safe === '/org' || safe.startsWith('/org/')) return ROUTES.HOME
    return safe
  }
  return ROUTES.HOME
}

/** Shell home is always the family app. */
export function resolveAuthedHomePath(
  _roles: RoleEntry[],
  _guardianViewActive: boolean,
): string {
  return ROUTES.HOME
}
